/**
 * Partner Portal — Payment Details onboarding (Slice C — Feb 2026 July).
 *
 * Uses the existing Fernet-encrypted bank_details_service on the backend.
 * The 4-state payment lifecycle is:
 *
 *   not_supplied           → nothing on file yet
 *   submitted              → some fields filled but not complete
 *   verification_required  → complete, awaiting admin verification
 *   verified               → admin has independently confirmed → eligible for payouts
 *
 * Route is guarded — if Business Details are not complete we redirect
 * the partner to `/partner/business-details` first.
 *
 * Security posture (never break these):
 *   • Full account number is only ever fetched for the partner's OWN
 *     record via `GET /api/partner/bank-details`.
 *   • This form never logs or renders the plaintext elsewhere.
 *   • Masked view (`account_number_masked`) is used for display until
 *     the user explicitly toggles "show".
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Loader2, ShieldCheck, AlertTriangle, Info, Save,
  Eye, EyeOff, Lock, CheckCircle2, Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { PartnerShell } from "@/pages/partner/Dashboard";

const PAYMENT_STATUS_META = {
  not_supplied: {
    label: "Not supplied",
    tone: "bg-slate-100 text-slate-700 border-slate-200",
    desc: "Enter your bank account details to receive commission payouts.",
    icon: Info,
  },
  submitted: {
    label: "In progress",
    tone: "bg-amber-50 text-amber-800 border-amber-200",
    desc: "Some fields still need completing before verification.",
    icon: AlertTriangle,
  },
  verification_required: {
    label: "Awaiting verification",
    tone: "bg-blue-50 text-blue-800 border-blue-200",
    desc: "Details saved. AWARE finance will verify these before your first payout.",
    icon: ShieldCheck,
  },
  verified: {
    label: "Verified",
    tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    desc: "Payouts are now enabled — welcome aboard!",
    icon: CheckCircle2,
  },
};

const EMPTY_FORM = {
  account_name: "",
  bsb: "",
  account_number: "",
  payid: "",
  remittance_email: "",
  notes: "",
};

export default function PaymentDetailsPage() {
  useSEO({ title: "Payment Details — Aware Partner Portal", noindex: true, includeBaseline: false });
  // Local guard state — fetched directly instead of via context because
  // OnboardingStatusProvider only exists inside PartnerShell (further
  // down the tree), so we cannot rely on the context hook up here.
  const [businessOk, setBusinessOk] = useState(null); // null=loading, true=ok, false=redirect
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partner/business-details");
        setBusinessOk(data?.status === "complete");
      } catch {
        setBusinessOk(false);
      }
    })();
  }, []);

  const [form, setForm] = useState(EMPTY_FORM);
  const [remote, setRemote] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErr, setFieldErr] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partner/bank-details");
        setRemote(data);
        if (data.revealed) {
          // Partner is authorised to see their own plaintext.
          setForm({
            account_name:     data.revealed.account_name || "",
            bsb:              data.revealed.bsb || data.bsb || "",
            account_number:   data.revealed.account_number || "",
            payid:            data.revealed.payid || "",
            remittance_email: data.revealed.remittance_email || "",
            notes:            data.revealed.notes || "",
          });
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Unable to load payment details.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const paymentStatus = remote?.payment_status || "not_supplied";
  const meta = PAYMENT_STATUS_META[paymentStatus] || PAYMENT_STATUS_META.not_supplied;
  const StatusIcon = meta.icon;

  const displayAccount = useMemo(() => {
    if (showAccount) return form.account_number || "";
    return remote?.account_number_masked || "";
  }, [showAccount, form.account_number, remote?.account_number_masked]);

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));

  const onSave = async () => {
    setSaving(true); setError(null); setFieldErr({});
    try {
      const { data } = await api.put("/partner/bank-details", {
        account_name:     form.account_name,
        bsb:              form.bsb,
        account_number:   form.account_number,
        payid:            form.payid || null,
        remittance_email: form.remittance_email || null,
        notes:            form.notes || null,
      });
      setRemote(data);
      toast.success("Payment details saved. AWARE finance will verify before your first payout.");
      // Refetch to grab masked view (server may re-encrypt account_number)
      try {
        const fresh = await api.get("/partner/bank-details");
        setRemote(fresh.data);
      } catch { /* ignore */ }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.field) {
        setFieldErr({ [detail.field]: detail.message });
      } else {
        setError(typeof detail === "string" ? detail : "Failed to save payment details.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onClear = async () => {
    if (!window.confirm("Remove your bank details? Payouts will pause until you re-enter them.")) return;
    setClearing(true); setError(null);
    try {
      const { data } = await api.delete("/partner/bank-details", { data: { reason: "partner_cleared" } });
      setRemote(data);
      setForm(EMPTY_FORM);
      setShowAccount(false);
      toast.success("Bank details removed.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to clear.");
    } finally {
      setClearing(false);
    }
  };

  // ── Guard: business details must be complete first ────────────
  if (businessOk === null) {
    return (
      <PartnerShell active="payment details">
        <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking onboarding status…
        </div>
      </PartnerShell>
    );
  }
  if (businessOk === false) {
    // Redirect + toast so the user knows why.
    toast.error("Please finish your Business Details before adding payment details.");
    return <Navigate to="/partner/business-details" replace />;
  }

  if (loading) {
    return (
      <PartnerShell active="payment details">
        <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading payment details…
        </div>
      </PartnerShell>
    );
  }

  return (
    <PartnerShell active="payment details">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="payment-details-page">
        <div>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Partner Onboarding</div>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Payment details</h1>
            </div>
            <span
              data-testid="payment-details-status-pill"
              className={`text-xs px-2.5 py-1 rounded-full border font-medium inline-flex items-center gap-1.5 ${meta.tone}`}
            >
              <StatusIcon className="w-3.5 h-3.5" />
              {meta.label}
            </span>
          </div>
          <p className="mt-3 text-sm text-slate-600 max-w-xl">{meta.desc}</p>
        </div>

        {/* Security assurance strip */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-cyan-50 to-blue-50 p-4 flex items-start gap-3">
          <Lock className="w-5 h-5 text-cyan-700 flex-none mt-0.5" />
          <div className="text-sm text-slate-700">
            <b>Your details are encrypted at rest</b> with AES-128 + HMAC (Fernet).
            Only your account name, BSB (never sensitive on its own) and the last four
            digits of your account number are readable by the AWARE team without an
            explicit reveal action — which is audit-logged every time.
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm flex items-start gap-2" data-testid="payment-details-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" /> {error}
          </div>
        )}

        {/* Form */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="text-base font-semibold text-slate-900">Bank account (AU)</h2>

          <div>
            <label className="text-xs font-medium text-slate-600">Account name *</label>
            <Input
              data-testid="payment-details-account-name"
              value={form.account_name}
              onChange={(e) => patch({ account_name: e.target.value })}
              placeholder="Noosa Water Pty Ltd"
              className="mt-1"
            />
            {fieldErr.account_name && <p className="text-xs text-rose-700 mt-1">{fieldErr.account_name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">BSB *</label>
              <Input
                data-testid="payment-details-bsb"
                value={form.bsb}
                onChange={(e) => patch({ bsb: e.target.value.replace(/[^0-9-]/g, "").slice(0, 7) })}
                placeholder="123-456"
                className="mt-1 font-mono"
                inputMode="numeric"
              />
              {fieldErr.bsb && <p className="text-xs text-rose-700 mt-1">{fieldErr.bsb}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Account number *</label>
              <div className="mt-1 flex gap-2">
                <Input
                  data-testid="payment-details-account-number"
                  type={showAccount ? "text" : "password"}
                  value={showAccount ? form.account_number : (form.account_number || displayAccount)}
                  onChange={(e) => patch({ account_number: e.target.value.replace(/[^0-9]/g, "").slice(0, 10) })}
                  placeholder="12345678"
                  className="font-mono"
                  inputMode="numeric"
                />
                <Button
                  type="button"
                  variant="outline"
                  data-testid="payment-details-toggle-visibility"
                  onClick={() => setShowAccount((v) => !v)}
                  className="flex-none"
                >
                  {showAccount ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {fieldErr.account_number && <p className="text-xs text-rose-700 mt-1">{fieldErr.account_number}</p>}
              {remote?.account_number_masked && !showAccount && !form.account_number && (
                <p className="text-[11px] text-slate-500 mt-1">On file: {remote.account_number_masked}</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">PayID (optional)</label>
            <Input
              data-testid="payment-details-payid"
              value={form.payid}
              onChange={(e) => patch({ payid: e.target.value })}
              placeholder="finance@example.com.au"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Remittance email (optional)</label>
            <Input
              data-testid="payment-details-remittance-email"
              value={form.remittance_email}
              onChange={(e) => patch({ remittance_email: e.target.value })}
              placeholder="finance@yourcompany.com.au"
              className="mt-1"
              type="email"
            />
            {fieldErr.remittance_email && <p className="text-xs text-rose-700 mt-1">{fieldErr.remittance_email}</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Notes for the AWARE finance team (optional)</label>
            <textarea
              data-testid="payment-details-notes"
              value={form.notes}
              onChange={(e) => patch({ notes: e.target.value.slice(0, 500) })}
              placeholder="Any special payment instructions"
              className="mt-1 w-full min-h-[80px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              maxLength={500}
            />
          </div>
        </section>

        {/* Verification status card */}
        {remote?.has_bank_details && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Verification status</h2>
            <div className="flex items-start gap-3 text-sm">
              {remote.verified ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none" />
                  <div>
                    <div className="font-medium text-emerald-800">Verified by AWARE finance</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Verified {remote.verified_at ? new Date(remote.verified_at).toLocaleString("en-AU") : "—"}
                      {remote.verified_by_email ? <> by {remote.verified_by_email}</> : null}
                    </div>
                    {remote.verification_note && (
                      <div className="text-xs text-slate-500 mt-1 italic">&ldquo;{remote.verification_note}&rdquo;</div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5 text-blue-600 flex-none" />
                  <div>
                    <div className="font-medium text-blue-900">Awaiting AWARE finance verification</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Editing any field will re-set your verification status. Payouts pause until we re-verify.
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* Save row */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="text-xs text-slate-500">
            {remote?.last_updated_at ? (
              <>Last saved {new Date(remote.last_updated_at).toLocaleString("en-AU")}</>
            ) : "Not saved yet"}
          </div>
          <div className="flex items-center gap-2">
            {remote?.has_bank_details && (
              <Button
                type="button"
                variant="outline"
                data-testid="payment-details-clear-btn"
                onClick={onClear}
                disabled={clearing || saving}
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
              >
                {clearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Remove
              </Button>
            )}
            <Button
              data-testid="payment-details-save-btn"
              onClick={onSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-cyan-700 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? "Saving…" : "Save payment details"}
            </Button>
          </div>
        </div>
      </div>
    </PartnerShell>
  );
}
